<template>
	<div class="pa-3">
		<div class="d-flex align-center mb-3">
			<!-- DCTL-2124: who else is on this project. Replaces the old per-column
			count chips (the counts still live on each kanban column header). Self
			is filtered out (otherAssignees) - this is "the OTHER assigned users". -->
			<template v-if="otherAssignees.length">
				<span class="text-caption text-grey-darken-2 mr-2"> Assigned: </span>
				<div class="d-flex flex-wrap ga-1">
					<v-chip
						v-for="person in visibleAssignees"
						:key="person.uuid"
						size="small"
						variant="flat"
						prepend-icon="mdi-account-outline"
						class="text-truncate bg-surface-tint text-fg-1"
						style="max-width: 200px"
					>
						{{ personLabel(person) }}
					</v-chip>
					<v-chip
						v-if="hiddenAssigneeCount"
						link
						size="small"
						variant="outlined"
						class="text-fg-1"
						@click="assigneesExpanded = !assigneesExpanded"
					>
						{{
							assigneesExpanded ? "Show less" : `+${hiddenAssigneeCount} more`
						}}
					</v-chip>
				</div>
			</template>
			<span v-else class="text-caption text-grey-darken-2">
				No other users assigned to this project
			</span>
			<v-spacer />
			<v-btn
				size="small"
				variant="text"
				:loading="loading"
				prepend-icon="mdi-refresh"
				@click="fetchDetail"
			>
				Refresh
			</v-btn>
		</div>

		<!-- DCTL-2133: be explicit about how the toolbar term narrows each column.
		Available searches the full claimable pool server-side, while My Papers and
		Complete filter the cards already loaded on this page. -->
		<div
			v-if="searchTerm"
			class="d-flex align-center mb-3 text-caption text-grey-darken-2"
		>
			<v-progress-circular
				v-if="availableSearching"
				indeterminate
				size="14"
				width="2"
				color="primary"
				class="mr-2 flex-shrink-0"
			/>
			<v-icon v-else size="small" class="mr-2 flex-shrink-0">
				mdi-magnify
			</v-icon>
			<span>
				Available shows all claimable papers matching "{{ searchTerm }}"; My
				Papers and Complete are filtered on this page.
			</span>
		</div>

		<v-overlay
			:model-value="loading"
			contained
			class="align-center justify-center"
		>
			<v-progress-circular color="primary" indeterminate size="64" />
		</v-overlay>

		<kanban-board
			v-if="!loading"
			:columns="columns"
			:items="filteredItems"
			:counts="counts"
			@card-action="() => {}"
		>
			<template #card="{ item, column }">
				<sde-kanban-card
					:item="item"
					:column-id="column.id"
					:disabled="submitting"
					@claim="handleClaim"
					@release="handleRelease"
					@open="startOrContinue"
					@view="handleView"
					@reopened="handleReopened"
				/>
			</template>
		</kanban-board>
	</div>
</template>

<script>
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import { useLDFlag } from "launchdarkly-vue-client-sdk";
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

import { KanbanBoard } from "@/components/kanban";
import SdeKanbanCard from "@/pages/SdeProjectDashboard/SdeKanbanCard";
import { PAPER_PREPARING, PAPER_PREPARING_MESSAGE } from "@/constants";
import { NEUTRAL_STATE } from "@/constants/workflowStates";

// SDE per-project board. Three columns: Available (claimable) | My Papers
// (everything on my plate) | My Completed (my own completed extractions -
// DCTL-2136). "My Papers" merges what the API returns
// as three separate buckets - not-started (assigned), started (in progress)
// and reopened (rework) - since assigned/rework are usually low-volume and
// don't each warrant a column. The cards inside it are told apart by a
// workState-driven status chip (see SdeKanbanCard). DCTL-2257: colours come from
// the shared workflow-state map, not per-board hexes - `state` is the column's
// workflow state and KanbanColumn resolves it to the tint/ink triplet.
// Collapsed cap for the header's assignee chips: enough to fill roughly one
// row at common widths. Beyond it the strip renders a "+N more" toggle chip
// instead of wrapping to several rows (large projects run to 25+ members).
const MAX_ASSIGNEE_CHIPS = 8;

const COLUMNS = [
	{
		id: "available",
		title: "Available",
		state: "available",
		icon: "mdi-inbox-arrow-down-outline",
	},
	{
		// A merged column: assigned + in progress + rework. DCTL-2279: neutral
		// chrome, because a column holding three states may not wear the hue of
		// one of them. This used to take the in-flight state on the reasoning that
		// "the papers are in my hands" - that is the CUSTODY argument, and REV
		// 2.1.1 (DCTL-2276) ruled that custody is not the workflow axis's job. It
		// is the same reason In QC shares orange with In progress: the test is
		// whether work is happening, not whose name is on it. Since these three
		// members span three buckets, the column is off the axis, which is exactly
		// what NEUTRAL_STATE is for. Each card still names its own state - after
		// 2.1.1 they read neutral / orange / rust, so the hue lives where it can
		// be true.
		id: "my_papers",
		title: "My Papers",
		state: NEUTRAL_STATE,
		icon: "mdi-clipboard-text-outline",
	},
	{
		// DCTL-2136: user-level "My Completed" - the requester's own completed
		// extractions (id/key stays `complete`; the backend serves my-completed).
		id: "complete",
		title: "My Completed",
		state: "complete",
		icon: "mdi-check-circle-outline",
	},
];

export default {
	name: "ProjectTab",

	components: {
		KanbanBoard,
		SdeKanbanCard,
	},

	props: {
		projectUuid: {
			type: String,
			required: true,
		},
		projectName: {
			type: String,
			default: "",
		},
		// Term from the dashboard's shared toolbar search (project-tab mode).
		// My Papers and Complete are filtered client-side by paper name; the
		// Available column is server-capped, so its search goes to the server
		// (DCTL-2133) over the full claimable pool.
		externalSearch: {
			type: String,
			default: "",
		},
	},

	setup() {
		// Editor selection for a brand-new extraction; a started entry always
		// reopens in its stored format (see startOrContinue).
		const useSdeV2 = useLDFlag("sde-v2");
		return { useSdeV2 };
	},

	data() {
		return {
			columns: COLUMNS,
			items: {},
			// DCTL-2124: the project's members (from my-detail), rendered as
			// assignee chips in the board header.
			projectMembers: [],
			loading: false,
			submitting: false,
			// DCTL-2133: server-side Available search. null = no active search
			// (show my-detail's capped preview); an array = the full-pool
			// results for the current term.
			availableSearchResults: null,
			availableSearching: false,
			// Assignee strip collapsed/expanded state; collapsed caps the chips
			// at MAX_ASSIGNEE_CHIPS so large teams don't push the board down
			// several rows. Reset on project switch.
			assigneesExpanded: false,
		};
	},

	computed: {
		// The board's rendered cards for the active toolbar term. My Papers and
		// Complete are the user's own bounded lists, so they're filtered by paper
		// name client-side. Available is server-capped, so its search is routed to
		// the server over the full claimable pool (DCTL-2133): when a term is
		// active we render `availableSearchResults` instead of the capped preview.
		filteredItems() {
			// Coerce null: a `clearable` v-text-field emits null (not "") on
			// clear, and Vue applies the String prop default only for undefined.
			const term = (this.externalSearch || "").trim().toLowerCase();
			if (!term) return this.items;

			const narrow = (list) =>
				(list || []).filter((item) =>
					(item.paperName || "").toLowerCase().includes(term),
				);
			return {
				// null = results not in yet (debounce/in-flight); render empty
				// rather than the stale capped preview so we never imply the
				// preview is the full match set.
				available: this.availableSearchResults ?? [],
				my_papers: narrow(this.items.my_papers),
				complete: narrow(this.items.complete),
			};
		},
		counts() {
			const result = {};
			for (const col of this.columns) {
				result[col.id] = (this.filteredItems[col.id] || []).length;
			}
			return result;
		},

		// DCTL-2133: the active, trimmed toolbar term (empty string when none).
		searchTerm() {
			return (this.externalSearch || "").trim();
		},

		// The current user, filtered out of the assignee chips (this is "who
		// else is on this project"). Matches SdeKanbanCard's self-filter.
		currentUserUuid() {
			return useMainStore().user.uuid;
		},

		// Other project members shown as chips in the board header: everyone
		// assigned to the project except me.
		otherAssignees() {
			return this.projectMembers.filter(
				(person) => person.uuid !== this.currentUserUuid,
			);
		},

		// The assignee chips actually rendered: all of them when expanded,
		// otherwise the first MAX_ASSIGNEE_CHIPS (roughly one header row).
		visibleAssignees() {
			return this.assigneesExpanded
				? this.otherAssignees
				: this.otherAssignees.slice(0, MAX_ASSIGNEE_CHIPS);
		},

		// How many chips the collapsed view hides; 0 means the team fits the
		// cap and no toggle is rendered (identical to the old always-on view).
		hiddenAssigneeCount() {
			return Math.max(0, this.otherAssignees.length - MAX_ASSIGNEE_CHIPS);
		},
	},

	watch: {
		projectUuid() {
			// Switching projects invalidates any in-flight Available search;
			// reload the board and re-run the search (if any) for the new project.
			this.debouncedAvailableSearch.cancel();
			this.availableSearchResults = null;
			this.assigneesExpanded = false;
			this.fetchDetail();
			this.handleSearchChange();
		},
		externalSearch() {
			this.handleSearchChange();
		},
	},

	created() {
		// DCTL-2133: debounce the Available server search so typing doesn't fire
		// a request per keystroke. Created here (not in data) so each instance
		// gets its own debounced fn with a stable `this`.
		this.debouncedAvailableSearch = _.debounce(this.fetchAvailableSearch, 300);
	},

	mounted() {
		this.fetchDetail();
		// Honor a term already present when the tab mounts.
		this.handleSearchChange();
	},

	beforeUnmount() {
		this.debouncedAvailableSearch.cancel();
	},

	methods: {
		...mapActions(useMainStore, [
			"setPaper",
			"setSaveUUID",
			"showNotification",
		]),

		// DCTL-2124: label for an assignee chip; email fallback (mirrors
		// SdeKanbanCard.personLabel).
		personLabel(person) {
			const name = `${person.firstName || ""} ${person.lastName || ""}`.trim();
			return name || person.email || "";
		},

		fetchDetail() {
			// Boolean, not the "secondary" color-string idiom: this flag feeds
			// v-overlay's model-value, which is strictly Boolean (the string
			// idiom is only valid on v-card/v-btn loading props).
			this.loading = true;
			this.$http
				.get(`/projects/${this.projectUuid}/my-detail`)
				.then((response) => {
					const cols = response.data.columns || {};
					// DCTL-2124: project members for the board's assignee chips.
					this.projectMembers = response.data.project?.members || [];
					// The API still returns five semantic buckets; the board
					// renders three. "My Papers" merges the SDE's own work
					// (not-started, started, reopened) and stamps each card with
					// a `workState` so it can pick the right accent, status chip
					// and actions. Order within it: rework first (a reviewer is
					// waiting), then in progress, then not-started.
					const tag = (list, workState) =>
						(list || []).map((item) => ({ ...item, workState }));
					this.items = {
						available: tag(cols.available, "available"),
						my_papers: [
							...tag(cols.rework, "rework"),
							...tag(cols.inProgress, "in_progress"),
							...tag(cols.assigned, "assigned"),
						],
						complete: tag(cols.complete, "complete"),
					};
				})
				.catch((error) => this.handleError(error))
				.finally(() => {
					this.loading = false;
				});
		},

		// DCTL-2133: react to a change in the toolbar term. An empty term reverts
		// the Available column to my-detail's capped preview (results = null); a
		// non-empty term debounces a full-pool server search.
		handleSearchChange() {
			const term = (this.externalSearch || "").trim();
			if (!term) {
				this.debouncedAvailableSearch.cancel();
				this.availableSearchResults = null;
				this.availableSearching = false;
				return;
			}
			this.availableSearching = true;
			this.debouncedAvailableSearch(term);
		},

		// DCTL-2133: fetch the full claimable pool matching `term` for this
		// project. Guarded against stale responses (the term or project may have
		// changed while the request was in flight).
		fetchAvailableSearch(term) {
			const projectUuid = this.projectUuid;
			this.$http
				.get(`/projects/${projectUuid}/available`, {
					params: { search: term },
				})
				.then((response) => {
					if (
						projectUuid !== this.projectUuid ||
						term !== (this.externalSearch || "").trim()
					) {
						return; // a newer search superseded this one
					}
					this.availableSearchResults = response.data.available || [];
				})
				.catch((error) => this.handleError(error))
				.finally(() => {
					this.availableSearching = false;
				});
		},

		// DCTL-2133: reload the board after a mutating action. fetchDetail
		// refreshes this.items, but when an Available search is active the column
		// renders the server results (availableSearchResults), so resync those
		// too - otherwise a just-claimed paper would linger in the search results.
		refreshBoard() {
			this.fetchDetail();
			if (this.searchTerm) {
				this.fetchAvailableSearch(this.searchTerm);
			}
		},

		handleClaim(item) {
			// DCTL-2134: refresh on failure too. When two curators race for the
			// same paper the loser gets a 409, and the paper is no longer
			// claimable - so the stale Available card must disappear rather than
			// linger and invite another doomed click.
			this.postAction(`papers/${item.paperUuid}/claim`, {
				refreshOnError: true,
			});
		},

		handleRelease(item) {
			this.postAction(`papers/${item.paperUuid}/release`);
		},

		postAction(pathSuffix, { refreshOnError = false } = {}) {
			this.submitting = true;
			this.$http
				.post(`/projects/${this.projectUuid}/${pathSuffix}`)
				.then(() => this.refreshBoard())
				.catch((error) => {
					this.handleError(error);
					// DCTL-2134: resync the board after a failed claim so the
					// now-unavailable card is removed (scoped to the claim path via
					// refreshOnError; release keeps the pre-existing behavior).
					// refreshBoard owns its own error handling, so a follow-on
					// failure won't report noisily.
					if (refreshOnError) this.refreshBoard();
				})
				.finally(() => {
					this.submitting = false;
				});
		},

		async startOrContinue(item) {
			this.submitting = true;
			try {
				const { data } = await this.$http.get(
					`/papers/${item.paperUuid}/start-info`,
				);
				this.setPaper(data.paper);
				// DCTL-2181: prefer THIS card's own extraction identity. A user can own
				// multiple extractions for one paper (two curators' extractions both
				// reassigned to them); start-info only resolves ONE (newest by created_at),
				// which may not be the one this card represents. Fall back to start-info only
				// for cards with no entry (assigned/available -> fresh uuid).
				const sdeUuid =
					item.dataEntryUuid ?? data.existingDataEntry?.uuid ?? uuidv4();
				this.setSaveUUID(sdeUuid);

				// A started entry reopens in its stored format; a new one uses the
				// sde-v2 flag (mirrors PaperTable.startDataEntry). ClinicalTrials.gov
				// papers (nct_id set) are SDEv2-only regardless of the flag.
				const startedFormat =
					item.dataFormat ?? data.existingDataEntry?.dataFormat;
				const useV2 = startedFormat
					? startedFormat === "DACTYL_SDE"
					: this.useSdeV2 || !!data.paper?.nct_id;
				this.$router.push(
					useV2
						? `/source-data-extraction/${item.paperUuid}/${sdeUuid}`
						: `/data-entry/${item.paperUuid}/${sdeUuid}`,
				);
			} catch (error) {
				this.handleError(error);
				// AI pre-extraction readiness gate: start-info refuses to start
				// a paper whose pre-extraction is still running. The board
				// hides such papers, so this card is stale - resync it.
				if (error.response?.data?.code === PAPER_PREPARING) {
					this.refreshBoard();
				}
			} finally {
				this.submitting = false;
			}
		},

		// DCTL-2132: open the paper in the read-only viewer, restoring the old
		// queue's "View Paper (Read-only)" preview. Mirrors
		// PaperTable.startReadOnlyDataEntry: reuse start-info (as startOrContinue
		// does) to resolve the user's existing entry, then pick the SDEv2 vs
		// legacy read-only route the same way. A paper with no entry yet
		// (Available, Assigned, or a Complete card the current user did not
		// personally extract - start-info only returns the requester's own
		// entry) opens a fresh read-only viewer per the sde-v2 flag.
		async handleView(item) {
			this.submitting = true;
			try {
				// readOnly: the viewer never saves, so start-info skips the AI
				// pre-extraction readiness gate (which refuses to START an
				// extraction on a paper still being prepared).
				const { data } = await this.$http.get(
					`/papers/${item.paperUuid}/start-info`,
					{ params: { readOnly: true } },
				);
				this.setPaper(data.paper);
				// DCTL-2181: prefer THIS card's own extraction identity (see
				// startOrContinue). start-info only resolves ONE entry (newest by
				// created_at), which may not be the one this card represents.
				const sdeUuid =
					item.dataEntryUuid ?? data.existingDataEntry?.uuid ?? uuidv4();
				this.setSaveUUID(sdeUuid);

				const startedFormat =
					item.dataFormat ?? data.existingDataEntry?.dataFormat;
				const useV2 = startedFormat
					? startedFormat === "DACTYL_SDE"
					: this.useSdeV2;
				this.$router.push(
					useV2
						? `/source-data-extraction-read-only/${item.paperUuid}/${sdeUuid}`
						: `/data-entry-read-only/${item.paperUuid}/${sdeUuid}`,
				);
			} catch (error) {
				this.handleError(error);
			} finally {
				this.submitting = false;
			}
		},

		// DCTL-2135: a card reopened its completed extraction; it now belongs in
		// rework, so resync the board (and any active Available search).
		handleReopened() {
			this.refreshBoard();
		},

		handleError(error) {
			// AI pre-extraction readiness gate (start-info, claim): the paper
			// is not ready yet rather than failed, so say it in the shared words.
			if (error.response?.data?.code === PAPER_PREPARING) {
				this.showNotification({
					message: PAPER_PREPARING_MESSAGE,
					status: "info",
				});
			} else if (error.response?.data?.message) {
				this.showNotification(error.response.data);
			} else if (error.response?.data?.error) {
				this.showNotification(error.response.data.error);
			} else {
				this.showNotification({
					message: "Something went wrong. Please try again.",
					status: "error",
				});
			}
		},
	},
};
</script>

<style scoped lang="scss">
// Keep the board inside the viewport so the page itself does not scroll,
// but never let a short window squash the columns to nothing: below the
// floor each column body keeps a usable height and scrolls internally
// instead (min-height wins over max-height). The offset covers the app bar,
// the SDE toolbar + tabs, this tab's assignee-chip row and the card's padding;
// scoped to the SDE board so the manager / QC boards are untouched.
:deep(.kanban-column__body) {
	max-height: calc(100vh - 360px) !important;
	min-height: 240px;
}
</style>
